// src/components/products/Wishlist.jsx
import React, { useEffect } from 'react'
import { AiFillHeart, AiOutlineHeart, AiOutlineShoppingCart } from 'react-icons/ai'
import { FaFemale } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  get_wishlist_products,
  remove_wishlist,
  add_to_card,
  messageClear,
} from '../../store/reducers/cardReducer'
import Ratings from '../Ratings'
import toast from 'react-hot-toast'

const Wishlist = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { userInfo } = useSelector((s) => s.auth)
  const { wishlist, successMessage, errorMessage } = useSelector((s) => s.card)

  // Load wishlist on mount
  useEffect(() => {
    if (userInfo) dispatch(get_wishlist_products(userInfo.id))
  }, [dispatch, userInfo])

  // Show toasts & refresh
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage)
      if (userInfo) dispatch(get_wishlist_products(userInfo.id))
      dispatch(messageClear())
    }
    if (errorMessage) {
      toast.error(errorMessage)
      dispatch(messageClear())
    }
  }, [dispatch, successMessage, errorMessage, userInfo])

  const handleAddToCart = (p, e) => {
    e.stopPropagation()
    if (!userInfo) return navigate('/login')
    dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: p._id }))
  }

  const handleToggleWish = (p, e) => {
    e.stopPropagation()
    if (!userInfo) return navigate('/login')
    dispatch(remove_wishlist(p._id))
  }

  if (!wishlist.length) {
    return (
      <div className="w-full text-center py-20 text-gray-500">
        Your wishlist is empty.
      </div>
    )
  }

  return (
    <div className="w-full px-4">
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {wishlist.map((p) => {
          const discounted = p.discount
            ? p.price - Math.floor((p.price * p.discount) / 100)
            : p.price

          return (
            <div
              key={p._id}
              onClick={() => navigate(`/product/details/${p.slug}`)}
              className="bg-gray-100 rounded shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden flex flex-col"
            >
              {/* IMAGE */}
              <div className="relative aspect-[3.4/4] overflow-hidden bg-gray-200">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {/* Heart */}
                <button
                  onClick={(e) => handleToggleWish(p, e)}
                  title="Remove from wishlist"
                  className="absolute top-2 left-2 p-1 bg-white rounded-full hover:bg-rose-50 transition-colors"
                >
                  <AiFillHeart size={18} className="text-pink-500" />
                </button>
                {/* Discount badge */}
                {p.discount > 0 && (
                  <div className="absolute bottom-2 left-2 bg-white bg-opacity-80 px-2 py-1 rounded text-xs font-semibold text-red-500">
                    -{p.discount}%
                  </div>
                )}
              </div>

              {/* INFO */}
              <div className="p-3 flex flex-col flex-grow">
                <h2 className="text-sm font-semibold text-gray-800 line-clamp-2">
                  {p.name}
                </h2>
                {p.shopName && (
                  <p className="text-xs text-gray-500">{p.shopName}</p>
                )}
                {p.description && (
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                    {p.description}
                  </p>
                )}

                {/* Price & Discount */}
                <div className="mt-2 flex items-baseline gap-2">
                  {p.discount > 0 ? (
                    <>
                      <span className="line-through text-gray-500 text-sm">
                        ₹{p.price}
                      </span>
                      <span className="text-base font-bold text-red-600">
                        ₹{discounted}
                      </span>
                    </>
                  ) : (
                    <span className="text-base font-bold">₹{p.price}</span>
                  )}
                </div>

                {/* Rating */}
                <div className="flex items-center text-xs text-gray-600 mt-1">
                  <Ratings ratings={parseFloat(p.rating) || 0} />
                  <span className="ml-1">({p.totalReview || 0})</span>
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    onClick={(e) => handleAddToCart(p, e)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100 transition"
                  >
                    <AiOutlineShoppingCart className="text-lg" />
                    <span>Add to cart</span>
                  </button>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100 transition"
                  >
                    <FaFemale className="text-lg" />
                    <span>Model Swap</span>
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default Wishlist
